import {notEmpty, notNil, traverse} from '@megaphonenpm/mputils/src/utils.js';
import {get} from 'lodash-es';
import {SCENE_TYPE_QUIZ} from './constants.js';

export class PlaylistService {
  constructor (dataStore) {
    this.dataStore = dataStore;
  }

  getPlaylistByID (id) {
    let playlists = get(this.dataStore, "playlists", []);
    return playlists.find(playlist => playlist.id === id);
  }

  getSceneByID (id) {
    let scenes = get(this.dataStore, "playlist.scenes");
    return notNil(scenes) ? scenes.find(scene => scene.id === id) : null;
  }

  getSceneIndexForID (id) {
    let scenes = get(this.dataStore, "playlist.scenes");
    return notNil(scenes) ? scenes.findIndex(scene => scene.id === id) : null;
  }

  getSceneIndex (scene) {
    return notNil(scene) ? this.getSceneIndexForID(scene.id) : null;
  }

  /**
   * Returns a Set of unique screen URIs for the specified playlist. Includes URIs for all instances
   * of media objects where media.screen.uri is not empty.
   *
   * @param playlist A JSON playlist object.
   * @param clientType One of the client-type contants, such as CLIENT_TYPE_SCREEN or CLIENT_TYPE_CONTROLLER.
   * @returns {Set<any>} A Set of media URLs for the specified clientType.
   */
  getPlaylistMediaURIs (playlist, clientType) {
    // Playlists often contain the same media URI multiple times (for example when a question
    // has several answers with the same image). To avoid duplication in the list of URIs returned
    // by getPlaylistScreenMediaURIs(), use a Set instead of an Array. A Set guarantees the list
    // of URIs has no duplicates.
    let URIs = new Set();

    // Function to invoke on every property in the playlist
    let hasMediaForClientType = (key, value, obj) => {
      let regex = new RegExp('\\.(jpg|jpeg|gif|png|mp4|mov|webm)$');
      if (key === 'media' || key === 'avatar') {
        // Found a 'media' property. Now check if it has a valid screen media URI...
        let uriPropertyValue = get(value, `${clientType}.uri`);
        if (typeof uriPropertyValue === 'string' && notEmpty(uriPropertyValue)) {
          // Strip hash and query string, and then look for file-extension matches
          let uriObject = new URL(uriPropertyValue);
          uriObject.hash = '';
          uriObject.search = '';

          let uriStringWithoutHashAndSearch = uriObject.toString();

          if (uriStringWithoutHashAndSearch.toLowerCase().match(regex) !== null) {
            // media.screen.uri is not blank AND its value matches our
            // whitelist filter for valid URIs, so include this URL in the results
            URIs.add(uriPropertyValue);
          }
        }
      }
    };

    // Invoke hasScreenMedia() on every property in the entire playlist
    traverse(playlist, hasMediaForClientType);

    // Return results
    return URIs;
  }

  /**
   * Returns an array of quiz scenes found in the specified playlist. A "quiz scene" is a scene
   * whose type property is set to SCENE_TYPE_QUIZ.
   */
  getQuizScenes (playlist) {
    return playlist.scenes.filter(scene => {
      return scene.type === SCENE_TYPE_QUIZ;
    });
  }

  /**
   * Returns the index of the specified scene among the quiz scenes in the specified playlist.
   * For example, in a playlist with 10 scenes where scenes 3 and 7 are quiz scenes, the
   * "quiz scene index" of scene 3 is 0, and of scene 7 is 1.
   * If the specified scene is not a quiz scene, returns -1.
   */
  getQuizSceneIndex (playlist, sceneID) {
    return this.getQuizScenes(playlist).findIndex(scene => scene.id === sceneID);
  }
}

